<template>
  <div class="chat-input" ref="root">
    <!-- Preview dos anexos -->
    <div v-if="attachments.length" class="attach-preview">
      <div
        v-for="a in attachments"
        :key="a.localId"
        class="attach-card"
        :class="{ uploading: a.status === 'uploading', error: a.status === 'error' }"
      >
        <div class="thumb" v-if="a.isImage && a.previewUrl">
          <img :src="a.previewUrl" alt="preview">
        </div>
        <div class="thumb file" v-else>📄</div>

        <div class="meta">
          <div class="name" :title="a.name">{{ a.name }}</div>
          <div class="sub">
            <span v-if="a.status === 'uploading'">Enviando...</span>
            <span v-else-if="a.status === 'error'">Falhou</span>
            <span v-else>{{ prettySize(a.size) }}</span>
          </div>
        </div>

        <button class="remove" type="button" @click="removeAttachment(a.localId)">×</button>
      </div>
    </div>

    <div class="row">
      <!-- 📎 -->
      <button class="icon-btn" type="button" @click="openFileDialog" title="Anexar">📎</button>
      <input ref="file" class="file-input" type="file" multiple :accept="accept" @change="onFilePicked">

      <!-- 😊 -->
      <div class="emoji-wrap">
        <button class="icon-btn" type="button" @click="toggleEmoji" title="Emojis">😊</button>

        <div v-if="emojiOpen" class="emoji-popover">
          <emoji-picker
            :recent="recentEmojis"
            @select="onEmojiSelect"
            @close="closeEmoji"
          />
        </div>
      </div>

      <input
        ref="input"
        v-model="text"
        @keydown.enter.prevent="send"
        placeholder="Digite uma mensagem..."
      />

      <button class="send-btn" type="button" @click="send" :disabled="sendDisabled">
        {{ hasUploading ? 'Enviando...' : 'Enviar' }}
      </button>
    </div>
  </div>
</template>

<script>
import EmojiPicker from './EmojiPicker.vue'

var RECENT_KEY = 'chatPlugin_recentEmojis'

export default {
  name: 'ChatInput',
  components: { EmojiPicker },
  props: { roomId: { type: [String, Number], required: true } },
  data: function () {
    return {
      text: '',
      attachments: [],
      emojiOpen: false,
      recentEmojis: [],
      _onDocClick: null
    }
  },
  computed: {
    accept: function () {
      return ['image/*', 'application/pdf', '.doc,.docx', '.xls,.xlsx', 'text/plain'].join(',')
    },
    hasUploading: function () {
      return this.attachments.some(function (a) { return a.status === 'uploading' })
    },
    hasDone: function () {
      return this.attachments.some(function (a) { return a.status === 'done' && a.id })
    },
    sendDisabled: function () {
      if (this.hasUploading) return true
      var hasText = !!(this.text && this.text.trim())
      return !(hasText || this.hasDone)
    }
  },
  methods: {
    // ------------------ enviar ------------------
    send: function () {
      if (this.sendDisabled) return

      var content = (this.text || '').trim()
      var files = this.attachments
        .filter(function (a) { return a.status === 'done' && a.id })
        .map(function (a) { return a.id })

      this.$emit('send', { content: content, files: files })

      // limpa UI local
      this.text = ''
      this.clearAttachments()
      this.closeEmoji()
      this.focusInput()
    },

    // ------------------ anexos ------------------
    openFileDialog: function () {
      if (this.$refs.file) this.$refs.file.click()
    },

    onFilePicked: function (ev) {
      var input = ev.target
      var files = input && input.files ? Array.from(input.files) : []
      input.value = ''
      files.forEach(this.addAndUpload)
    },

    addAndUpload: function (file) {
      var localId = 'a_' + Date.now() + '_' + Math.random().toString(16).slice(2)
      var isImage = /^image\//.test(file.type || '')
      var previewUrl = null
      if (isImage) {
        try { previewUrl = URL.createObjectURL(file) } catch (e) {}
      }

      var item = {
        localId: localId,
        id: null,
        name: file.name || 'arquivo',
        size: file.size || 0,
        mimetype: file.type || '',
        url: null,
        isImage: isImage,
        previewUrl: previewUrl,
        status: 'uploading'
      }

      this.attachments.push(item)

      var api = (this.$chat && this.$chat.api)
        ? this.$chat.api
        : (this.$root.$chat ? this.$root.$chat.api : null)

      if (!api || !api.uploadFile) {
        item.status = 'error'
        return
      }

      api.uploadFile(file, this.roomId)
        .then((resp) => {
          var data = resp && resp.data ? resp.data : resp
          item.id = data && data.id ? data.id : null
          item.url = data && data.url ? data.url : null
          item.mimetype = data && (data.mimetype || data.mime) ? (data.mimetype || data.mime) : item.mimetype
          item.size = data && data.size != null ? data.size : item.size
          item.name = data && (data.name || data.originalName || data.filename) ? (data.name || data.originalName || data.filename) : item.name
          item.status = item.id ? 'done' : 'error'
        })
        .catch(() => {
          item.status = 'error'
        })
    },

    removeAttachment: function (localId) {
      var idx = this.attachments.findIndex(function (a) { return a.localId === localId })
      if (idx === -1) return

      var a = this.attachments[idx]
      if (a && a.previewUrl) {
        try { URL.revokeObjectURL(a.previewUrl) } catch (e) {}
      }
      this.attachments.splice(idx, 1)
    },

    clearAttachments: function () {
      this.attachments.forEach(function (a) {
        if (a && a.previewUrl) {
          try { URL.revokeObjectURL(a.previewUrl) } catch (e) {}
        }
      })
      this.attachments = []
    },

    prettySize: function (bytes) {
      var n = Number(bytes || 0)
      if (!n) return '0 B'
      if (n < 1024) return n + ' B'
      if (n < 1024 * 1024) return (n / 1024).toFixed(1) + ' KB'
      return (n / (1024 * 1024)).toFixed(1) + ' MB'
    },

    // ------------------ emoji ------------------
    toggleEmoji: function () {
      this.emojiOpen = !this.emojiOpen
      if (this.emojiOpen) {
        this.loadRecent()
        this.bindOutside()
      } else {
        this.unbindOutside()
      }
    },

    closeEmoji: function () {
      this.emojiOpen = false
      this.unbindOutside()
    },

    onEmojiSelect: function (emoji) {
      this.insertAtCursor(emoji)
      this.saveRecent(emoji)
      this.focusInput()
    },

    insertAtCursor: function (emoji) {
      var input = this.$refs.input
      if (!input) {
        this.text = (this.text || '') + emoji
        return
      }

      var value = this.text || ''
      var start = typeof input.selectionStart === 'number' ? input.selectionStart : value.length
      var end = typeof input.selectionEnd === 'number' ? input.selectionEnd : value.length

      this.text = value.slice(0, start) + emoji + value.slice(end)

      this.$nextTick(function () {
        try {
          var pos = start + emoji.length
          input.setSelectionRange(pos, pos)
        } catch (e) {}
      })
    },

    loadRecent: function () {
      try {
        var raw = window.localStorage.getItem(RECENT_KEY)
        var parsed = raw ? JSON.parse(raw) : []
        this.recentEmojis = Array.isArray(parsed) ? parsed.slice(0, 24) : []
      } catch (e) {
        this.recentEmojis = []
      }
    },

    saveRecent: function (emoji) {
      try {
        var list = Array.isArray(this.recentEmojis) ? this.recentEmojis.slice() : []
        list = [emoji].concat(list.filter(function (e) { return e !== emoji }))
        list = list.slice(0, 24)
        this.recentEmojis = list
        window.localStorage.setItem(RECENT_KEY, JSON.stringify(list))
      } catch (e) {}
    },

    bindOutside: function () {
      if (this._onDocClick) return
      var self = this
      this._onDocClick = function (ev) {
        var root = self.$refs.root
        if (!root) return
        if (root.contains(ev.target)) return
        self.closeEmoji()
      }
      document.addEventListener('mousedown', this._onDocClick, true)
      document.addEventListener('touchstart', this._onDocClick, true)
    },

    unbindOutside: function () {
      if (!this._onDocClick) return
      document.removeEventListener('mousedown', this._onDocClick, true)
      document.removeEventListener('touchstart', this._onDocClick, true)
      this._onDocClick = null
    },

    focusInput: function () {
      var el = this.$refs.input
      if (el && el.focus) el.focus()
    }
  },
  beforeDestroy: function () {
    this.clearAttachments()
    this.unbindOutside()
  }
}
</script>

<style scoped>
.chat-input { border-top: 1px solid #eee; background: #fff; padding: 10px 12px; }
.row { display: flex; align-items: center; gap: 8px; }

.icon-btn{
  width: 40px; height: 40px; border-radius: 20px;
  border: 1px solid #ddd; background: #fff; cursor: pointer;
  font-size: 18px; display:flex; align-items:center; justify-content:center;
}
.icon-btn:hover{ background:#f6f6f6; }
.file-input{ display:none; }

.emoji-wrap{ position: relative; }
.emoji-popover{
  position: absolute;
  bottom: 48px;
  left: 0;
  z-index: 50;
}

.chat-input input{
  flex:1; padding:10px 12px; border:1px solid #ddd; border-radius:20px; outline:none;
}

.send-btn{
  padding:10px 18px; border:none; border-radius:20px; cursor:pointer;
  background: linear-gradient(135deg,#9c27b0,#673ab7);
  color:#fff; font-weight:600;
}
.send-btn:disabled{ opacity:.5; cursor:not-allowed; }

/* Preview */
.attach-preview{ display:flex; gap:8px; flex-wrap:wrap; padding:0 0 10px; }
.attach-card{
  display:flex; align-items:center; gap:10px;
  border:1px solid #e6e6e6; border-radius:12px; padding:8px 10px;
  background:#fafafa; min-width:220px; max-width:320px; position:relative;
}
.attach-card.uploading{ opacity:.75; }
.attach-card.error{ border-color:#f2b8b8; background:#fff6f6; }

.thumb{ width:44px; height:44px; border-radius:10px; background:#f0f0f0; overflow:hidden; display:flex; align-items:center; justify-content:center; }
.thumb img{ width:100%; height:100%; object-fit:cover; }

.meta{ flex:1; min-width:0; }
.name{ font-weight:600; font-size:13px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.sub{ font-size:12px; color:#666; margin-top:2px; }

.remove{
  width:28px; height:28px; border-radius:14px; border:1px solid #ddd; background:#fff;
  cursor:pointer; font-size:18px; display:flex; align-items:center; justify-content:center;
}
.remove:hover{ background:#f3f3f3; }
</style>



// WEBPACK FOOTER //
// ChatInput.vue?40779f97